import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import DashboardLayout from '@/components/DashboardLayout';
import RubroChecklistSection from '@/components/RubroChecklistSection';
import WorkShiftsField from '@/components/WorkShiftsField';
import AuthImage from '@/components/AuthImage';
import { VoiceDictationButton } from '@/components/VoiceDictationButton';
import { ConnectionIndicator } from '@/components/ConnectionIndicator';
import { useConnectivity } from '@/hooks/useConnectivity';
import * as offline from '@/lib/offlineStore';
import { useAuth } from '@/context/AuthContext';
import api, { apiErrorMessage } from '@/lib/api';
import { shouldFilter, getClientIndustry, filterTemplateByIndustry } from '@/lib/industryFilter';
import { toast } from 'sonner';
import {
    ArrowLeft, Save, ChevronDown, ChevronRight, ClipboardList,
    Camera, Loader2, CheckCircle2, XCircle, MinusCircle, AlertTriangle, FileText, Sparkles, Lock, Send, AlertCircle,
    UploadCloud, HardDriveDownload,
} from 'lucide-react';

const ACCENT = '#7C6BEF';
const WEIGHT_LABEL = { 1: 'Estándar', 2: 'NOM', 3: 'Crítico' };
const WEIGHT_COLOR = { 1: '#9CA3AF', 2: '#3B82F6', 3: '#EF4444' };

// Iter 110 · Fase B — compresión ligera SOLO para la copia offline (reduce el
// consumo de almacenamiento del dispositivo). Redimensiona a máx 1920px y
// re-codifica a JPEG 0.82. Si algo falla, devuelve el archivo original. El
// flujo ONLINE no usa esto: sube el archivo tal cual, idéntico a hoy.
async function compressImageForOffline(file) {
    try {
        const bitmap = await createImageBitmap(file);
        const MAX = 1920;
        let { width, height } = bitmap;
        if (width > MAX || height > MAX) {
            const scale = Math.min(MAX / width, MAX / height);
            width = Math.round(width * scale);
            height = Math.round(height * scale);
        }
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        canvas.getContext('2d').drawImage(bitmap, 0, 0, width, height);
        const blob = await new Promise((res) => canvas.toBlob(res, 'image/jpeg', 0.82));
        bitmap.close?.();
        return blob && blob.size > 0 ? blob : file;
    } catch {
        return file; // fallback: guarda el original
    }
}

export default function InspectionForm() {
    const { id } = useParams();
    const { user } = useAuth();
    const isReadOnly = user?.role !== 'inspector';
    const Layout = user?.role === 'inspector' ? DashboardLayout : AdminLayout;
    const layoutProps = user?.role === 'inspector' ? { role: 'inspector' } : { title: 'Formulario de Auditoría' };
    const backLink = user?.role === 'inspector' ? '/inspector' : '/admin/inspections';
    const backLabel = user?.role === 'inspector' ? 'Volver al dashboard' : 'Volver a Auditorías';
    const [template, setTemplate] = useState(null);
    const [inspection, setInspection] = useState(null); // {request, client, standard, ...}
    const [aiSection, setAiSection] = useState({ items: [], industry: null, generated_at: null });
    const [generatingAi, setGeneratingAi] = useState(false);
    const [responses, setResponses] = useState({ general_data: {}, answers: {}, observations: {}, photos: {} });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [openSection, setOpenSection] = useState('general');
    const [lastSavedAt, setLastSavedAt] = useState(null);   // ISO timestamp of last successful save
    const [saveError, setSaveError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
    const [submittedStatus, setSubmittedStatus] = useState(null); // 'in_review' after submit
    // Iter 108 · Modo OFFLINE Fase A — persistencia local + sincronización.
    const [offlinePending, setOfflinePending] = useState(false);  // hay respuestas locales sin subir
    const [syncing, setSyncing] = useState(false);                // subiendo lo pendiente
    const [syncError, setSyncError] = useState(null);             // falló la subida
    // Iter 110 · Fase B — fotos offline: progreso de subida y aviso de espacio.
    const [photoProgress, setPhotoProgress] = useState(null);     // {done,total} durante sync de fotos
    const [storageWarn, setStorageWarn] = useState(null);         // % usado cuando el disco va lleno
    const storageWarnedRef = React.useRef(false);
    // Iter 99 · Modo OFFLINE Fase 1 — estado de conexión real (solo relevante para el auditor).
    const { status: connStatus, isOnline, checkNow } = useConnectivity();
    const prevOnlineRef = useRef(true);
    useEffect(() => {
        if (isReadOnly) return;
        if (connStatus === 'offline' && prevOnlineRef.current) {
            prevOnlineRef.current = false;
        } else if (connStatus === 'online' && !prevOnlineRef.current) {
            prevOnlineRef.current = true;
            toast.success('Conexión recuperada. Subiendo automáticamente tu avance guardado…');
        }
    }, [connStatus, isReadOnly]);
    const [loadError, setLoadError] = useState(null);
    const [reloadTick, setReloadTick] = useState(0);
    const [adminFeedback, setAdminFeedback] = useState({}); // { item_id: {admin_comment, ai_opinion, ai_opinion_at} }
    // Iter 42 — Industry filter snapshot. Locked once when the audit finishes
    // loading. Never recomputed while the auditor is working, so re-tagging
    // the master template mid-flow can't remove content the auditor already
    // saw. See `frontend/src/lib/industryFilter.js` for the pure logic.
    const [industryFilterActive, setIndustryFilterActive] = useState(null); // null = undecided
    const dirtyRef = React.useRef(false);
    const responsesRef = React.useRef(responses);
    responsesRef.current = responses;
    const savingRef = React.useRef(false);
    savingRef.current = saving;

    useEffect(() => {
        // Iter 91 · resetear el estado de carga al cambiar de auditoría — antes
        // `loading` no volvía a `true` si se navegaba de una auditoría a otra
        // sin recarga completa (Link/navigate cliente-side), dejando visibles
        // por un instante los datos de la auditoría ANTERIOR bajo la URL nueva.
        setLoading(true);
        setLoadError(null);
        setTemplate(null);
        setInspection(null);
        setResponses({ general_data: {}, answers: {}, observations: {}, photos: {} });
        setAiSection({ items: [], industry: null, generated_at: null });
        setAdminFeedback({});
        setSubmittedStatus(null);
        setIndustryFilterActive(null);

        // Iter 91 · CRÍTICO: en campo (conexión inestable del auditor), un
        // único blip de red en ESTA llamada específica dejaba el formulario
        // completamente vacío sin aviso claro — no tenía `.catch()` propio, así
        // que si fallaba, `setResponses` nunca se ejecutaba y el formulario
        // quedaba en su estado inicial (todo vacío) con `loading=false` de
        // todos modos, como si la auditoría nunca se hubiera guardado. Ahora
        // reintenta una vez y, si sigue fallando, bloquea la vista con un
        // error explícito en vez de mostrar datos vacíos silenciosamente.
        const fetchResponsesWithRetry = () =>
            api.get(`/inspections/${id}/responses`).catch(() => api.get(`/inspections/${id}/responses`));

        // Iter 108 · Fase A — carga con respaldo OFFLINE.
        // 1) Intento normal ONLINE (idéntico a hoy). Si funciona: guardo un
        //    snapshot en IndexedDB para poder reabrir sin señal, y si hay
        //    respuestas locales pendientes (de una sesión previa sin señal) las
        //    fusiono encima. 2) Si la carga falla (sin señal), hidrato la
        //    auditoría desde el snapshot local y sigo trabajando offline.
        const mergePending = (base, pending) => ({
            ...base,
            general_data: { ...(base.general_data || {}), ...(pending.general_data || {}) },
            answers: { ...(base.answers || {}), ...(pending.answers || {}) },
            observations: { ...(base.observations || {}), ...(pending.observations || {}) },
        });

        (async () => {
            try {
                const [tplR, certR, respR, aiR, fbR] = await Promise.all([
                    api.get(`/checklist-template?inspection_id=${id}`),
                    api.get(`/certifications/${id}`),
                    fetchResponsesWithRetry(),
                    api.get(`/inspections/${id}/ai-questions`).catch(() => ({ data: {} })),
                    api.get(`/inspections/${id}/admin-feedback`).catch(() => ({ data: {} })),
                ]);
                setTemplate(tplR.data);
                setInspection(certR.data);
                const st = certR.data?.request?.status;
                if (st === 'in_review' || st === 'approved' || st === 'rejected') setSubmittedStatus(st);
                const aiSec = {
                    items: aiR.data.items || [],
                    industry: aiR.data.industry || null,
                    generated_at: aiR.data.generated_at || null,
                };
                setAiSection(aiSec);
                const feedback = fbR.data?.items || {};
                setAdminFeedback(feedback);
                let resp = {
                    general_data: respR.data.general_data || {},
                    answers: respR.data.answers || {},
                    observations: respR.data.observations || {},
                    photos: respR.data.photos || {},
                };
                // Fusiona respuestas locales pendientes (sobreviven a recargas).
                const rec = await offline.getRecord(id);
                if (rec?.pending && !st) {
                    resp = mergePending(resp, rec.pending);
                    setOfflinePending(true);
                }
                setResponses(resp);
                // Snapshot para reabrir sin señal esta auditoría YA abierta.
                offline.saveSnapshot(id, {
                    template: tplR.data, inspection: certR.data, aiSection: aiSec,
                    adminFeedback: feedback,
                    submittedStatus: ['in_review', 'approved', 'rejected'].includes(st) ? st : null,
                    responses: { ...resp, photos: {} },
                });
            } catch (e) {
                // Sin señal: intenta hidratar desde el snapshot local.
                const rec = await offline.getRecord(id);
                if (rec?.snapshot) {
                    const s = rec.snapshot;
                    setTemplate(s.template);
                    setInspection(s.inspection);
                    setAiSection(s.aiSection || { items: [], industry: null, generated_at: null });
                    setAdminFeedback(s.adminFeedback || {});
                    if (s.submittedStatus) setSubmittedStatus(s.submittedStatus);
                    let resp = s.responses || { general_data: {}, answers: {}, observations: {}, photos: {} };
                    if (rec.pending) { resp = mergePending(resp, rec.pending); setOfflinePending(true); }
                    setResponses(resp);
                    toast.info('Sin conexión: abriste la auditoría con tu copia local. Tu avance se guarda en este dispositivo y se subirá al recuperar señal.', { duration: 7000 });
                } else {
                    setLoadError(apiErrorMessage(e, 'No se pudo cargar la auditoría. Verifica tu conexión.'));
                    toast.error(apiErrorMessage(e, 'Error al cargar'));
                }
            } finally {
                setLoading(false);
            }
        })();
        // Expose inspection id to nested components via a namespaced global (simpler than prop drilling)
        window.__inspectionId = id;
    }, [id, reloadTick]);

    // Mark audit as in_progress on first open (auditor only). Idempotent on the backend.
    useEffect(() => {
        if (loading) return;
        if (user?.role !== 'inspector') return;
        if (submittedStatus) return; // already past the fill-in stage
        api.post(`/inspections/${id}/open`).catch(() => { /* best-effort */ });
    }, [loading, id, user?.role, submittedStatus]);

    // Lock the industry-filter decision exactly once, after all initial fetches
    // resolved. `responsesRef.current` holds the just-loaded server state, so
    // if the audit already has any answers we bypass the filter (show all).
    useEffect(() => {
        if (industryFilterActive !== null) return; // decided already
        if (loading) return;                        // still fetching
        if (!inspection) return;                    // safety
        const active = shouldFilter({
            inspection,
            responses: responsesRef.current,
            submittedStatus,
        });
        setIndustryFilterActive(active);
    }, [loading, inspection, industryFilterActive, submittedStatus]);

    // Filtered template — respects the locked-in decision above.
    const filteredTemplate = useMemo(() => {
        if (!template) return null;
        if (!industryFilterActive) return template;
        return filterTemplateByIndustry(template, getClientIndustry(inspection));
    }, [template, industryFilterActive, inspection]);

    const itemsById = useMemo(() => {
        const m = {};
        (filteredTemplate?.sections || []).forEach((s) => s.items.forEach((it) => { m[it.id] = it; }));
        return m;
    }, [filteredTemplate]);

    const generateAiQuestions = async () => {
        setGeneratingAi(true);
        try {
            const r = await api.post(`/inspections/${id}/ai-questions`);
            setAiSection({
                items: r.data.items || [],
                industry: r.data.industry,
                generated_at: r.data.generated_at,
            });
            toast.success(`${r.data.items?.length || 0} preguntas específicas generadas por IA`);
            setOpenSection('ai');
        } catch (err) {
            toast.error(apiErrorMessage(err, 'Error al generar preguntas IA'));
        } finally {
            setGeneratingAi(false);
        }
    };

    const totals = useMemo(() => {
        if (!filteredTemplate) return { items: 0, answered: 0, totalWeight: 0, score: 0, pct: 0 };
        let items = 0, answered = 0, totalWeight = 0, score = 0;
        const countItem = (it) => {
            items++;
            const ans = responses.answers[it.id];
            const w = it.weight;
            if (ans === 'na') { /* exclude */ }
            else {
                totalWeight += w;
                if (ans) { answered++; if (ans === 'yes') score += w; }
            }
        };
        filteredTemplate.sections.forEach((s) => s.items.forEach(countItem));
        aiSection.items.forEach(countItem);
        const pct = totalWeight > 0 ? (score / totalWeight) * 100 : 0;
        return { items, answered, totalWeight, score, pct };
    }, [filteredTemplate, responses.answers, aiSection.items]);

    // Iter 65 · UI de ítems bloqueantes. El backend ya fuerza REPROBADO cuando
    // un blocking=true recibe "no" (routes/inspections._compute_score). Aquí
    // solo hacemos visible ese comportamiento para el auditor: contamos los
    // bloqueantes en scope, listamos cuáles ya cayeron y pedimos confirmación
    // consciente al marcar "no" en uno de ellos.
    const blockingStats = useMemo(() => {
        if (!filteredTemplate) return { total: 0, failed: [] };
        let total = 0;
        const failed = [];
        filteredTemplate.sections.forEach((s) => s.items.forEach((it) => {
            if (!it.blocking) return;
            total++;
            if (responses.answers[it.id] === 'no') failed.push({ id: it.id, question: it.question });
        }));
        return { total, failed };
    }, [filteredTemplate, responses.answers]);

    // Confirm modal state for blocking "no" answers.
    const [pendingBlockingNo, setPendingBlockingNo] = useState(null); // { itemId, question } | null

    const setAnswer = (itemId, val) => setResponses((r) => ({ ...r, answers: { ...r.answers, [itemId]: val } }));
    // Wraps setAnswer with a confirm modal when the auditor picks "no" on a blocking item.
    // Non-blocking items and any non-"no" answer keep the original zero-friction path.
    const setAnswerWithBlockingGuard = React.useCallback((item, val) => {
        if (val === 'no' && item?.blocking && responses.answers[item.id] !== 'no') {
            setPendingBlockingNo({ itemId: item.id, question: item.question });
            return;
        }
        setAnswer(item.id, val);
    }, [responses.answers]);
    const setObs = (itemId, val) => setResponses((r) => ({ ...r, observations: { ...r.observations, [itemId]: val } }));
    const setGen = (k, v) => setResponses((r) => ({ ...r, general_data: { ...r.general_data, [k]: v } }));
    const setPhoto = (itemId, dataUrl) => setResponses((r) => ({ ...r, photos: { ...r.photos, [itemId]: dataUrl } }));

    // Admin-only three-layer feedback renderer. Returns null for non-admins so the panel is hidden.
    const isAdmin = user?.role === 'admin';
    const renderAdminFeedback = React.useCallback((itemId) => {
        if (!isAdmin) return null;
        const obs = (responsesRef.current?.observations || {})[itemId] || '';
        // Only show for items with an auditor observation OR that already have admin feedback,
        // to keep the panel focused on "áreas de oportunidad" and avoid clutter.
        const existing = adminFeedback[itemId];
        if (!obs && !existing) return null;
        return (
            <AdminFeedbackPanel
                itemId={itemId}
                initial={existing}
                onCommentPersist={(iid, data) => setAdminFeedback((prev) => ({ ...prev, [iid]: data }))}
                onAiPersist={(iid, data) => setAdminFeedback((prev) => ({ ...prev, [iid]: data }))}
            />
        );
    }, [isAdmin, adminFeedback]);

    const [uploadingPhotoId, setUploadingPhotoId] = useState(null);

    // Iter 110 · Fase B — aviso proactivo de espacio: avisa al llegar a 80% ANTES
    // de que sea un problema. A ~95% se bloquea el guardado con mensaje explícito.
    const maybeWarnStorage = async () => {
        const est = await offline.estimateStorage();
        if (!est || !est.quota) return est;
        if (est.percent >= 80) {
            setStorageWarn(est.percent);
            if (!storageWarnedRef.current) {
                storageWarnedRef.current = true;
                toast.warning(`Espacio del dispositivo al ${est.percent}%. Recupera señal pronto para subir y liberar las fotos guardadas.`, { duration: 8000 });
            }
        } else {
            setStorageWarn(null);
        }
        return est;
    };

    // Guarda la foto en este dispositivo (blob comprimido) cuando no hay señal.
    const storeOfflinePhoto = async (itemId, file, item) => {
        setUploadingPhotoId(itemId);
        try {
            const blob = await compressImageForOffline(file);
            const est = await offline.estimateStorage();
            if (est && est.quota && est.freeBytes < blob.size + 1_000_000) {
                toast.error('No hay espacio suficiente en el dispositivo para guardar la foto. Recupera señal para subir y liberar las fotos ya tomadas.', { duration: 9000 });
                return false;
            }
            await offline.savePhoto(id, itemId, {
                blob, question: item.question || '', standard_reference: item.standard_reference || '',
                mime: 'image/jpeg',
            });
            const objectUrl = URL.createObjectURL(blob);
            setResponses((r) => ({ ...r, photos: { ...r.photos, [itemId]: { localObjectUrl: objectUrl, pending: true } } }));
            setOfflinePending(true);
            toast.info('Foto guardada en este dispositivo. Se subirá sola al recuperar señal.', { duration: 5000 });
            await maybeWarnStorage();
            return true;
        } catch (err) {
            const q = err?.name === 'QuotaExceededError' || /quota|exceeded/i.test(String(err?.message || err));
            toast.error(q
                ? 'El dispositivo se quedó sin espacio: no se pudo guardar la foto. Recupera señal para subir y liberar las fotos ya tomadas.'
                : 'No se pudo guardar la foto en este dispositivo.', { duration: 9000 });
            return false;
        } finally {
            setUploadingPhotoId(null);
        }
    };

    const onPhotoChange = async (itemId, e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (!f.type.startsWith('image/')) { toast.error('Sólo imágenes'); return; }
        const item = itemsById[itemId] || {};

        // Iter 110 · Fase B — con señal REAL sube igual que hoy; sin señal, guarda
        // la foto en este dispositivo y la sube sola al reconectar (capa adicional).
        const reallyOnline = await checkNow();
        if (!reallyOnline) {
            await storeOfflinePhoto(itemId, f, item);
            return;
        }
        if (f.size > 4 * 1024 * 1024) { toast.error('Máx. 4 MB'); return; }
        setUploadingPhotoId(itemId);
        try {
            const fd = new FormData();
            fd.append('file', f);
            fd.append('item_id', itemId);
            fd.append('question', item.question || '');
            fd.append('answer', responsesRef.current.answers[itemId] || '');
            fd.append('observations', responsesRef.current.observations[itemId] || '');
            fd.append('standard_reference', item.standard_reference || '');
            const r = await api.post(`/inspections/${id}/analyze-photo`, fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setPhoto(itemId, { url: r.data.photo_url });
            await offline.deletePhoto(id, itemId); // por si había una copia local
            toast.success('Foto guardada');
        } catch (err) {
            if (!err.response) {
                // La señal se cayó a media subida → respaldo local, sin perder la foto.
                await storeOfflinePhoto(itemId, f, item);
            } else {
                toast.error(apiErrorMessage(err, 'Error al subir la foto'));
            }
        } finally {
            setUploadingPhotoId(null);
        }
    };

    const saveDraft = async ({ silent = false } = {}) => {
        if (isReadOnly) {
            if (!silent) toast.error('Solo el auditor asignado puede calificar');
            return { ok: false };
        }
        if (submittedStatus) {
            if (!silent) toast.error('La auditoría ya fue enviada; no se pueden guardar cambios.');
            return { ok: false };
        }
        // Iter 108 · Fase A — guardia de conexión REAL. Sin señal, el avance NO
        // se pierde: se guarda en este dispositivo (IndexedDB) y se sube solo al
        // recuperar señal. El guardado ONLINE queda EXACTAMENTE igual que antes.
        const reallyOnline = await checkNow();
        if (!reallyOnline) {
            const { photos, ...local } = responsesRef.current;
            await offline.savePending(id, local);
            setOfflinePending(true);
            setSaveError(null);
            dirtyRef.current = false;
            if (!silent) toast.info('Sin señal: tu avance se guardó en este dispositivo. Se subirá solo al recuperar conexión.', { duration: 6000 });
            return { ok: false, offline: true, savedLocal: true };
        }
        if (savingRef.current) return { ok: false, busy: true };
        setSaving(true);
        setSaveError(null);
        try {
            const r = await api.put(`/inspections/${id}/responses`, responsesRef.current);
            const now = new Date().toISOString();
            setLastSavedAt(now);
            dirtyRef.current = false;
            // Subida OK → ya no hay copia local pendiente.
            await offline.clearPending(id);
            setOfflinePending(false);
            setSyncError(null);
            if (!silent) toast.success(`Guardado · ${r.data.computed?.percent_compliance ?? 0}% cumplimiento`);
            return { ok: true, data: r.data };
        } catch (err) {
            if (!err.response) {
                // Fallo de RED estando "en línea": respaldo local, sin error rojo.
                const { photos, ...local } = responsesRef.current;
                await offline.savePending(id, local);
                setOfflinePending(true);
                setSaveError(null);
                if (!silent) toast.info('No se pudo subir ahora; tu avance quedó guardado en este dispositivo. Reintentaré al recuperar señal.', { duration: 6000 });
                return { ok: false, offline: true, savedLocal: true };
            }
            const msg = apiErrorMessage(err, 'Error al guardar');
            setSaveError(msg);
            if (!silent) toast.error(msg);
            return { ok: false, error: msg };
        } finally { setSaving(false); }
    };

    // Auto-save with 2s debounce whenever responses change (inspector only).
    // Uses refs to avoid stale-closure issues.
    useEffect(() => {
        if (loading || isReadOnly || submittedStatus) return;
        dirtyRef.current = true;
        const t = setTimeout(() => {
            if (dirtyRef.current) saveDraft({ silent: true });
        }, 2000);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [responses, loading, isReadOnly, submittedStatus]);

    // Iter 108 · Fase A — persistencia INMEDIATA sin señal. El autosave espera
    // 2s; si el auditor cierra la app justo tras responder, ese cambio podría
    // perderse. Estando offline (o con pendientes), escribimos a IndexedDB al
    // instante en cada cambio, garantizando que nada se pierda al cerrar/reabrir.
    useEffect(() => {
        if (loading || isReadOnly || submittedStatus) return;
        if (!isOnline || offlinePending) {
            const { photos, ...local } = responsesRef.current;
            offline.savePending(id, local);
            if (!isOnline) setOfflinePending(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [responses, isOnline, loading, isReadOnly, submittedStatus]);

    // Iter 108 · Fase A — motor de SINCRONIZACIÓN. Sube lo pendiente al servidor
    // al recuperar señal. Con reintento y feedback; nunca falla en silencio.
    const syncingRef = React.useRef(false);
    const syncPending = React.useCallback(async () => {
        if (isReadOnly || submittedStatus) return;
        if (syncingRef.current) return;
        const rec = await offline.getRecord(id);
        const pendingPhotos = await offline.getPhotosForInspection(id);
        if (!rec?.pending && pendingPhotos.length === 0) { setOfflinePending(false); return; }
        const reallyOnline = await checkNow();
        if (!reallyOnline) return; // aún sin señal; se reintenta en el próximo latido
        syncingRef.current = true;
        setSyncing(true);
        setSyncError(null);
        let photoFailures = [];
        try {
            // 1) Sube las FOTOS pendientes primero, con progreso y reintento.
            if (pendingPhotos.length > 0) {
                setPhotoProgress({ done: 0, total: pendingPhotos.length });
                let done = 0;
                for (const p of pendingPhotos) {
                    let ok = false;
                    for (let attempt = 0; attempt < 2 && !ok; attempt++) {
                        try {
                            const fd = new FormData();
                            fd.append('file', new File([p.blob], `${p.itemId}.jpg`, { type: p.mime || 'image/jpeg' }));
                            fd.append('item_id', p.itemId);
                            fd.append('question', p.question || '');
                            fd.append('answer', responsesRef.current.answers[p.itemId] || '');
                            fd.append('observations', responsesRef.current.observations[p.itemId] || '');
                            fd.append('standard_reference', p.standard_reference || '');
                            const pr = await api.post(`/inspections/${id}/analyze-photo`, fd, {
                                headers: { 'Content-Type': 'multipart/form-data' },
                            });
                            // Confirmado en servidor → recién ahí borramos la copia local.
                            await offline.deletePhoto(id, p.itemId);
                            setPhoto(p.itemId, { url: pr.data.photo_url });
                            ok = true;
                        } catch (perr) {
                            if (perr.response) break; // error de servidor (no de red): no reintentar en bucle
                        }
                    }
                    if (ok) { done += 1; setPhotoProgress({ done, total: pendingPhotos.length }); }
                    else photoFailures.push(p.itemId);
                }
                setPhotoProgress(null);
                await maybeWarnStorage(); // recalcula el espacio tras liberar fotos
            }

            // 2) Sube las RESPUESTAS/observaciones pendientes.
            if (rec?.pending) {
                const payload = {
                    ...responsesRef.current,
                    general_data: { ...(responsesRef.current.general_data || {}), ...(rec.pending.general_data || {}) },
                    answers: { ...(responsesRef.current.answers || {}), ...(rec.pending.answers || {}) },
                    observations: { ...(responsesRef.current.observations || {}), ...(rec.pending.observations || {}) },
                };
                const r = await api.put(`/inspections/${id}/responses`, payload);
                await offline.clearPending(id);
                dirtyRef.current = false;
                setLastSavedAt(new Date().toISOString());
                if (photoFailures.length === 0) {
                    toast.success(`Avance sincronizado · ${r.data.computed?.percent_compliance ?? 0}% cumplimiento`, { duration: 5000 });
                }
            }

            if (photoFailures.length > 0) {
                setSyncError(`No se pudieron subir ${photoFailures.length} foto(s). Reintentaremos.`);
                setOfflinePending(true);
                toast.error(`Quedaron ${photoFailures.length} foto(s) pendientes de subir (ítems: ${photoFailures.join(', ')}). Se reintentará automáticamente.`, { duration: 8000 });
            } else {
                setOfflinePending(false);
                if (pendingPhotos.length > 0 && !rec?.pending) {
                    toast.success('Todas las fotos se subieron al servidor.', { duration: 5000 });
                }
            }
        } catch (err) {
            setPhotoProgress(null);
            setSyncError('No se pudo subir el avance guardado. Reintentaremos.');
            setOfflinePending(true);
            toast.error('No se pudo subir tu avance guardado localmente. Se reintentará automáticamente.', { duration: 6000 });
        } finally {
            syncingRef.current = false;
            setSyncing(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, isReadOnly, submittedStatus, checkNow]);

    // Iter 110 · Fase B — hidrata las MINIATURAS de fotos offline al abrir (para
    // que el auditor vea sus fotos sin señal, no un espacio vacío).
    useEffect(() => {
        if (loading || isReadOnly) return;
        let cancelled = false;
        (async () => {
            const photos = await offline.getPhotosForInspection(id);
            if (cancelled || photos.length === 0) return;
            setResponses((r) => {
                const next = { ...r.photos };
                let changed = false;
                for (const p of photos) {
                    if (!next[p.itemId]?.url && !next[p.itemId]?.localObjectUrl) {
                        next[p.itemId] = { localObjectUrl: URL.createObjectURL(p.blob), pending: true };
                        changed = true;
                    }
                }
                return changed ? { ...r, photos: next } : r;
            });
            setOfflinePending(true);
            maybeWarnStorage();
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, id, isReadOnly]);

    // Dispara sync al montar/estar en línea con pendientes, y en cada latido online.
    useEffect(() => {
        if (loading || isReadOnly || submittedStatus) return;
        if (isOnline) syncPending();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOnline, loading, offlinePending, isReadOnly, submittedStatus]);

    // Flush a save when the user navigates away or the tab is hidden — safety net for tablets.
    useEffect(() => {
        if (isReadOnly || submittedStatus) return;
        const flush = () => {
            if (dirtyRef.current) {
                // Best-effort sync save via sendBeacon-style; fetch will run in background.
                try { api.put(`/inspections/${id}/responses`, responsesRef.current).catch(() => {}); } catch { /* ignore */ }
            }
        };
        const onHide = () => { if (document.visibilityState === 'hidden') flush(); };
        window.addEventListener('beforeunload', flush);
        document.addEventListener('visibilitychange', onHide);
        return () => {
            window.removeEventListener('beforeunload', flush);
            document.removeEventListener('visibilitychange', onHide);
        };
    }, [id, isReadOnly, submittedStatus]);

    const submitFinal = async () => {
        setSubmitting(true);
        try {
            // Iter 99 · flush + connectivity guard — no enviar en silencio si no hay red real.
            const draft = await saveDraft({ silent: true });
            if (draft.offline) {
                toast.error('Sin conexión: no se pudo enviar la auditoría. Recupera señal e inténtalo de nuevo.', { duration: 6000 });
                setShowSubmitConfirm(false);
                return;
            }
            const r = await api.post(`/inspections/${id}/submit`);
            setSubmittedStatus(r.data?.status || 'in_review');
            toast.success('Auditoría enviada al Administrador para evaluación.');
            setShowSubmitConfirm(false);
        } catch (err) {
            toast.error(apiErrorMessage(err, 'Error al enviar'));
        } finally { setSubmitting(false); }
    };

    if (loadError) {
        return (
            <Layout {...layoutProps}>
                <div className="p-8 text-center border-2 border-dashed" style={{ borderColor: '#EF4444' }} data-testid="inspection-load-error">
                    <AlertCircle className="w-8 h-8 mx-auto mb-3" style={{ color: '#EF4444' }} />
                    <p className="font-bold mb-1">No se pudo cargar la auditoría</p>
                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">{loadError}</p>
                    <button onClick={() => setReloadTick((n) => n + 1)} data-testid="inspection-load-retry-btn"
                        className="inline-flex items-center gap-2 px-5 py-2.5 text-white font-bold" style={{ background: ACCENT }}>
                        Reintentar
                    </button>
                </div>
            </Layout>
        );
    }
    if (loading || !template || industryFilterActive === null) {
        return <Layout {...layoutProps}><div className="p-12 text-center text-gray-500" data-testid="inspection-loading">Cargando formulario...</div></Layout>;
    }

    const pctColor = totals.pct >= 90 ? '#10B981' : totals.pct >= 70 ? '#F59E0B' : '#EF4444';
    const missing = totals.items - totals.answered;

    return (
        <Layout {...layoutProps}>
            <Link to={backLink} className="inline-flex items-center gap-2 text-sm font-bold mb-5 hover:underline" style={{ color: ACCENT }}>
                <ArrowLeft className="w-4 h-4" /> {backLabel}
            </Link>

            {isReadOnly && (
                <div className="p-4 mb-5 border-l-4 flex items-start gap-3"
                    style={{ background: 'rgba(245,158,11,0.08)', borderColor: '#F59E0B' }}
                    data-testid="readonly-banner">
                    <Lock className="w-5 h-5 shrink-0 mt-0.5" style={{ color: '#F59E0B' }} />
                    <div>
                        <div className="font-bold text-sm">Vista solo lectura</div>
                        <div className="text-xs opacity-80">
                            Solo el auditor asignado puede calificar y modificar el checklist. Como administrador, puedes revisar el avance pero no editarlo.
                        </div>
                    </div>
                </div>
            )}

            {/* Progress card */}
            <div className="p-5 mb-6" style={{ background: 'var(--card-bg)', border: '1px solid var(--border)' }} data-testid="progress-card">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
                    <div className="font-display font-bold">Progreso del formulario</div>
                    <div className="flex items-center gap-3 text-sm">
                        <span className="text-gray-500">{totals.answered} / {totals.items} ítems</span>
                        <span className="text-gray-400">·</span>
                        <span className="px-3 py-1 font-bold text-white" style={{ background: pctColor }}>
                            {totals.pct.toFixed(1)}% cumplimiento
                        </span>
                    </div>
                </div>
                <div className="h-2 w-full bg-gray-200 dark:bg-gray-800 overflow-hidden">
                    <div className="h-full transition-all" style={{ width: `${totals.pct}%`, background: pctColor }} />
                </div>
                {missing > 0 && (
                    <div className="text-xs mt-3 inline-flex items-center gap-1" style={{ color: '#F59E0B' }}>
                        <AlertTriangle className="w-3.5 h-3.5" /> {missing} ítem(s) sin responder
                    </div>
                )}
            </div>

            {submittedStatus && (
                <div className="p-4 mb-5 border-l-4 flex items-start gap-3"
                    style={{ background: 'rgba(16,185,129,0.08)', borderColor: '#10B981' }}
                    data-testid="submitted-banner">
                    <CheckCircle2 className="w-5 h-5 shrink-0 mt-0.5" style={{ color: '#10B981' }} />
                    <div>
                        <div className="font-bold text-sm">Auditoría enviada al Administrador</div>
                        <div className="text-xs opacity-80">
                            {submittedStatus === 'in_review'
                                ? 'Estado: en revisión. El administrador dictaminará y comunicará el resultado al cliente.'
                                : `Estado final: ${{ approved: 'Aprobada', rejected: 'Rechazada' }[submittedStatus] || submittedStatus}.`}
                        </div>
                    </div>
                </div>
            )}

            {/* Iter 110 · Fase B — aviso proactivo de espacio del dispositivo */}
            {!isReadOnly && !submittedStatus && storageWarn != null && (
                <div className="mb-3 flex items-center gap-2 px-3 py-2 text-sm font-bold rounded-md"
                    style={{ background: 'rgba(245,158,11,0.15)', color: '#B45309', border: '1px solid rgba(245,158,11,0.5)' }}
                    data-testid="storage-warning">
                    <AlertTriangle className="w-4 h-4 shrink-0" />
                    Espacio del dispositivo al {storageWarn}%. Recupera señal pronto para subir las fotos guardadas y liberar espacio.
                </div>
            )}

            <div className="mb-6 flex items-center gap-3 flex-wrap sticky top-0 z-30 py-2 -mx-1 px-1"
                style={{ background: 'var(--bg, #ffffff)' }}>
                {/* Iter 99 · Modo OFFLINE Fase 1 — indicador permanente de conexión (solo auditor) */}
                {!isReadOnly && !submittedStatus && (
                    <ConnectionIndicator status={connStatus} offlinePending={offlinePending} syncing={syncing} syncError={syncError} photoProgress={photoProgress} />
                )}
                <button onClick={() => saveDraft()} disabled={saving || isReadOnly || !!submittedStatus}
                    data-testid="save-draft-btn"
                    title={isReadOnly ? 'Solo el auditor asignado puede guardar' : 'Guardar avance'}
                    className="inline-flex items-center gap-2 px-5 py-2.5 text-white font-bold disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{ background: ACCENT }}>
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    {isReadOnly ? 'Solo lectura' : 'Guardar avance'}
                </button>

                {/* Saved indicator */}
                {!isReadOnly && !submittedStatus && (
                    <span
                        data-testid="save-indicator"
                        className="inline-flex items-center gap-1.5 text-xs font-medium transition-opacity"
                        style={{
                            color: syncError ? '#B45309' : (offlinePending || syncing) ? '#1D4ED8' : saveError ? '#EF4444' : lastSavedAt ? '#10B981' : '#9CA3AF',
                            opacity: (saving || syncing || offlinePending || lastSavedAt || saveError || syncError) ? 1 : 0.5,
                        }}
                    >
                        {syncing ? (
                            <><UploadCloud className="w-3.5 h-3.5 animate-pulse" /> {photoProgress ? `Subiendo ${photoProgress.done} de ${photoProgress.total} fotos…` : 'Sincronizando avance…'}</>
                        ) : syncError ? (
                            <>
                                <AlertCircle className="w-3.5 h-3.5" /> {syncError}
                                <button type="button" onClick={() => syncPending()} data-testid="sync-retry-btn"
                                    className="ml-1 underline font-bold">Reintentar</button>
                            </>
                        ) : offlinePending ? (
                            <><HardDriveDownload className="w-3.5 h-3.5" /> Guardado en este dispositivo · pendiente de subir</>
                        ) : saving ? (
                            <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Guardando…</>
                        ) : saveError ? (
                            <><AlertCircle className="w-3.5 h-3.5" /> {saveError}</>
                        ) : lastSavedAt ? (
                            <><CheckCircle2 className="w-3.5 h-3.5" /> Guardado {formatSince(lastSavedAt)}</>
                        ) : (
                            <>Autoguardado activo</>
                        )}
                    </span>
                )}

                {/* Final submit button — only for inspector, only pre-submission */}
                {!isReadOnly && !submittedStatus && (
                    <button
                        type="button"
                        onClick={() => setShowSubmitConfirm(true)}
                        disabled={saving || submitting}
                        data-testid="submit-audit-btn"
                        className="ml-auto inline-flex items-center gap-2 px-5 py-2.5 text-white font-bold transition-opacity hover:opacity-90 disabled:opacity-40"
                        style={{ background: '#10B981' }}
                    >
                        <Send className="w-4 h-4" /> Guardar y enviar
                    </button>
                )}
            </div>

            {/* Iter 65 · Blocking items awareness banner — shown only when the
                scope contains at least one critical (blocking) item. Gives the
                auditor context up-front so a "no" late in the checklist isn't
                an unpleasant surprise. */}
            {blockingStats.total > 0 && !isReadOnly && (
                <div className="mb-5 p-4 border-l-4 flex items-start gap-3"
                    style={{
                        background: blockingStats.failed.length ? 'rgba(220,38,38,0.08)' : 'rgba(220,38,38,0.04)',
                        borderColor: '#DC2626',
                    }}
                    data-testid="blocking-summary-banner">
                    <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" style={{ color: '#DC2626' }} />
                    <div className="text-sm min-w-0">
                        <div className="font-bold" style={{ color: '#DC2626' }}>
                            {blockingStats.failed.length === 0 ? (
                                <>Esta auditoría incluye {blockingStats.total} ítem{blockingStats.total === 1 ? '' : 's'} crítico{blockingStats.total === 1 ? '' : 's'}</>
                            ) : (
                                <>⚠ {blockingStats.failed.length} de {blockingStats.total} ítem{blockingStats.total === 1 ? '' : 's'} crítico{blockingStats.total === 1 ? '' : 's'} marcado{blockingStats.failed.length === 1 ? '' : 's'} como {'"'}no cumple{'"'}</>
                            )}
                        </div>
                        <div className="text-xs opacity-80 mt-0.5">
                            Un {'"'}no cumple{'"'} en cualquiera de estos ítems fuerza el resultado <strong>REPROBADO</strong> automáticamente,
                            sin importar el score porcentual del resto del checklist.
                        </div>
                        {blockingStats.failed.length > 0 && (
                            <ul className="mt-2 text-xs space-y-0.5" data-testid="blocking-failed-list">
                                {blockingStats.failed.map((f) => (
                                    <li key={f.id} className="flex items-start gap-1.5">
                                        <span className="font-mono opacity-70">{f.id}</span>
                                        <span className="opacity-90 truncate">{f.question}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            )}

            {/* Datos de Relevamiento */}
            <Accordion
                id="general"
                open={openSection === 'general'}
                onToggle={() => setOpenSection(openSection === 'general' ? null : 'general')}
                icon={FileText}
                title="Datos de Relevamiento"
                count={`${Object.values(responses.general_data).filter(Boolean).length} / ${filteredTemplate.general_data_fields.length} completados`}
            >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {filteredTemplate.general_data_fields.map((field) => (
                        <GeneralField key={field.key} field={field}
                            value={responses.general_data[field.key]}
                            onChange={(v) => setGen(field.key, v)}
                            disabled={isReadOnly} />
                    ))}
                </div>
            </Accordion>

            {/* Sections */}
            {filteredTemplate.sections.map((sec) => {
                const answered = sec.items.filter((it) => responses.answers[it.id]).length;
                return (
                    <Accordion
                        key={sec.section_id}
                        id={sec.section_id}
                        open={openSection === sec.section_id}
                        onToggle={() => setOpenSection(openSection === sec.section_id ? null : sec.section_id)}
                        icon={ClipboardList}
                        title={`${sec.section_id} ${sec.title}`}
                        count={`${answered}/${sec.items.length}`}
                    >
                        <div className="space-y-4">
                            {sec.items.map((it) => (
                                <ChecklistItem key={it.id} item={it}
                                    answer={responses.answers[it.id]}
                                    observation={responses.observations[it.id] || ''}
                                    photo={responses.photos[it.id]}
                                    uploadingPhoto={uploadingPhotoId === it.id}
                                    disabled={isReadOnly}
                                    onAnswer={(v) => setAnswerWithBlockingGuard(it, v)}
                                    onObs={(v) => setObs(it.id, v)}
                                    onPhoto={(e) => onPhotoChange(it.id, e)}
                                    renderAdminFeedback={renderAdminFeedback} />
                            ))}
                        </div>
                    </Accordion>
                );
            })}

            {/* Rubro Checklist Section (con IA de fotos + multi-instancia) */}
            {inspection && (inspection.request?.industry || inspection.client?.industry) && (
                <RubroChecklistSection
                    inspectionId={id}
                    industry={inspection.request?.industry || inspection.client?.industry}
                    open={openSection === 'rubro'}
                    onToggle={() => setOpenSection(openSection === 'rubro' ? null : 'rubro')}
                    responses={responses}
                    onAnswer={setAnswer}
                    onObs={setObs}
                    disabled={isReadOnly}
                />
            )}

            {/* AI Supplemental Section */}
            <AiSection
                items={aiSection.items}
                industry={aiSection.industry}
                generatedAt={aiSection.generated_at}
                open={openSection === 'ai'}
                onToggle={() => setOpenSection(openSection === 'ai' ? null : 'ai')}
                onGenerate={generateAiQuestions}
                generating={generatingAi}
                responses={responses}
                onAnswer={setAnswer}
                onObs={setObs}
                onPhotoChange={onPhotoChange}
                renderAdminFeedback={renderAdminFeedback}
                disabled={isReadOnly}
            />
            {/* Submit confirmation */}
            {showSubmitConfirm && (
                <SubmitConfirmModal
                    onCancel={() => setShowSubmitConfirm(false)}
                    onConfirm={submitFinal}
                    submitting={submitting}
                    totals={totals}
                />
            )}
            {pendingBlockingNo && (
                <BlockingNoConfirmModal
                    item={pendingBlockingNo}
                    onCancel={() => setPendingBlockingNo(null)}
                    onConfirm={() => {
                        setAnswer(pendingBlockingNo.itemId, 'no');
                        setPendingBlockingNo(null);
                    }}
                />
            )}
        </Layout>
    );
}

function BlockingNoConfirmModal({ item, onCancel, onConfirm }) {
    return (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={onCancel} data-testid="blocking-no-confirm-modal">
            <div className="max-w-md w-full p-6 max-h-[calc(100vh-2rem)] overflow-y-auto" onClick={(e) => e.stopPropagation()}
                style={{ background: 'var(--card-bg)', border: '2px solid #DC2626' }}>
                <div className="flex items-start gap-3 mb-4">
                    <div className="w-10 h-10 flex items-center justify-center shrink-0"
                        style={{ background: 'rgba(220,38,38,0.15)', color: '#DC2626' }}>
                        <AlertTriangle className="w-5 h-5" />
                    </div>
                    <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: '#DC2626' }}>
                            Ítem crítico · confirmación requerida
                        </div>
                        <h3 className="font-display font-black text-lg mt-0.5">¿Marcar como {'"'}No cumple{'"'}?</h3>
                    </div>
                </div>
                <div className="text-sm mb-3">
                    <div className="text-xs font-mono opacity-70 mb-1">{item.itemId}</div>
                    <div className="opacity-90">{item.question}</div>
                </div>
                <div className="p-3 mb-4 text-xs"
                    style={{ background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.35)', color: '#DC2626' }}>
                    <strong>Al confirmar, la auditoría completa quedará marcada como REPROBADO</strong>
                    {' '}automáticamente al enviarla, sin importar el score porcentual del resto del checklist.
                    Esta regla aplica a todos los ítems críticos.
                </div>
                <div className="flex items-center justify-end gap-2">
                    <button type="button" onClick={onCancel}
                        data-testid="blocking-no-cancel"
                        className="px-4 py-2 border-2 text-sm font-bold"
                        style={{ borderColor: 'var(--border)' }}>
                        Cancelar
                    </button>
                    <button type="button" onClick={onConfirm}
                        data-testid="blocking-no-confirm"
                        className="px-4 py-2 text-white text-sm font-bold inline-flex items-center gap-2"
                        style={{ background: '#DC2626' }}>
                        <AlertTriangle className="w-4 h-4" /> Sí, marcar {'"'}No cumple{'"'}
                    </button>
                </div>
            </div>
        </div>
    );
}

function SubmitConfirmModal({ onCancel, onConfirm, submitting, totals }) {
    const missing = totals.items - totals.answered;
    return (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onCancel} data-testid="submit-confirm-modal">
            <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md" style={{ background: 'var(--card-bg)', border: '2px solid #10B981' }}>
                <div className="px-6 py-4 flex items-start gap-3 border-b" style={{ borderColor: 'var(--border)' }}>
                    <div className="w-10 h-10 flex items-center justify-center shrink-0" style={{ background: 'rgba(16,185,129,0.15)', color: '#10B981' }}>
                        <Send className="w-5 h-5" />
                    </div>
                    <div>
                        <h3 className="font-display text-lg font-bold">¿Enviar auditoría al Administrador?</h3>
                        <p className="text-sm text-gray-500 mt-1">
                            Se marcará como <span className="font-bold">en revisión</span> y el administrador podrá dictaminarla.
                            El resultado al cliente sigue siendo manual, no se enviará automáticamente.
                        </p>
                        {missing > 0 && (
                            <div className="mt-2 text-xs inline-flex items-center gap-1" style={{ color: '#F59E0B' }}>
                                <AlertTriangle className="w-3.5 h-3.5" /> Tienes {missing} ítem(s) sin responder.
                            </div>
                        )}
                    </div>
                </div>
                <div className="px-6 py-4 flex justify-end gap-2">
                    <button type="button" onClick={onCancel} disabled={submitting}
                        className="px-4 py-2 text-sm font-medium border hover:bg-black/5 dark:hover:bg-white/10 disabled:opacity-50"
                        style={{ borderColor: 'var(--border)' }}
                        data-testid="submit-cancel-btn">
                        Cancelar
                    </button>
                    <button type="button" onClick={onConfirm} disabled={submitting}
                        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
                        style={{ background: '#10B981' }}
                        data-testid="submit-confirm-btn">
                        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        Enviar
                    </button>
                </div>
            </div>
        </div>
    );
}

function formatSince(iso) {
    try {
        const diffSec = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
        if (diffSec < 5) return 'ahora';
        if (diffSec < 60) return `hace ${diffSec}s`;
        if (diffSec < 3600) return `hace ${Math.floor(diffSec / 60)} min`;
        return new Date(iso).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
}

function AiSection({ items, industry, generatedAt, open, onToggle, onGenerate, generating, responses, onAnswer, onObs, onPhotoChange, renderAdminFeedback, disabled = false }) {
    const answered = items.filter((it) => responses.answers[it.id]).length;
    const hasItems = items.length > 0;
    return (
        <div className="mb-3" style={{ background: 'var(--card-bg)', border: `1px solid ${ACCENT}` }} data-testid="section-ai">
            <button type="button" onClick={onToggle}
                className="w-full flex items-center justify-between gap-3 px-5 py-4 hover:bg-black/[0.02] dark:hover:bg-white/5">
                <div className="flex items-center gap-3 min-w-0">
                    {open ? <ChevronDown className="w-4 h-4 shrink-0" /> : <ChevronRight className="w-4 h-4 shrink-0" />}
                    <Sparkles className="w-4 h-4 shrink-0" style={{ color: ACCENT }} />
                    <span className="font-display font-bold text-sm text-left">
                        Preguntas específicas por rubro (IA)
                        {industry && <span className="ml-2 text-xs opacity-70">— {industry}</span>}
                    </span>
                </div>
                <span className="text-xs text-gray-500 shrink-0">
                    {hasItems ? `${answered}/${items.length}` : 'Sin generar'}
                </span>
            </button>
            {open && (
                <div className="px-5 pb-5 pt-2 border-t" style={{ borderColor: 'var(--border)' }}>
                    <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                        <div className="text-xs opacity-70 flex-1 min-w-[220px]">
                            {hasItems ? (
                                <>
                                    Preguntas generadas específicas del rubro. Complementan el checklist maestro.
                                    {generatedAt && <span className="block opacity-60 mt-0.5">Última generación: {new Date(generatedAt).toLocaleString('es')}</span>}
                                </>
                            ) : (
                                'La IA generará preguntas específicas para el rubro del cliente, tomando en cuenta la norma vinculada y sus documentos.'
                            )}
                        </div>
                        <button onClick={onGenerate} disabled={generating || disabled}
                            data-testid="generate-ai-questions-btn"
                            className="inline-flex items-center gap-2 px-4 py-2 text-white text-sm font-bold disabled:opacity-60"
                            style={{ background: ACCENT }}>
                            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                            {hasItems ? 'Regenerar con IA' : 'Generar con IA'}
                        </button>
                    </div>
                    {hasItems ? (
                        <div className="space-y-4">
                            {items.map((it) => (
                                <ChecklistItem key={it.id} item={it}
                                    answer={responses.answers[it.id]}
                                    observation={responses.observations[it.id] || ''}
                                    photo={responses.photos[it.id]}
                                    disabled={disabled}
                                    onAnswer={(v) => onAnswer(it.id, v)}
                                    onObs={(v) => onObs(it.id, v)}
                                    onPhoto={(e) => onPhotoChange(it.id, e)}
                                    renderAdminFeedback={renderAdminFeedback} />
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-8 opacity-60 text-sm border-dashed border-2 rounded" style={{ borderColor: 'var(--border)' }}>
                            Aún no se han generado preguntas IA para esta auditoría.
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function Accordion({ id, open, onToggle, icon: Icon, title, count, children }) {
    return (
        <div className="mb-3" style={{ background: 'var(--card-bg)', border: '1px solid var(--border)' }} data-testid={`section-${id}`}>
            <button type="button" onClick={onToggle}
                className="w-full flex items-center justify-between gap-3 px-5 py-4 hover:bg-black/[0.02] dark:hover:bg-white/5">
                <div className="flex items-center gap-3 min-w-0">
                    {open ? <ChevronDown className="w-4 h-4 shrink-0" /> : <ChevronRight className="w-4 h-4 shrink-0" />}
                    <Icon className="w-4 h-4 shrink-0" style={{ color: ACCENT }} />
                    <span className="font-display font-bold text-sm text-left">{title}</span>
                </div>
                <span className="text-xs text-gray-500 shrink-0">{count}</span>
            </button>
            {open && <div className="px-5 pb-5 pt-2 border-t" style={{ borderColor: 'var(--border)' }}>{children}</div>}
        </div>
    );
}

function GeneralField({ field, value, onChange, disabled = false }) {
    // Iter 83 · Turnos laborales reutiliza el mismo widget de 3 checkboxes
    // (Matutino/Vespertino/Nocturno) que ya existe en el perfil del cliente —
    // antes caía en el input de texto genérico y solo mostraba 1 campo libre.
    if (field.key === 'work_shifts') {
        return <WorkShiftsField value={value} onChange={onChange} disabled={disabled} testIdPrefix="general-workshift" />;
    }
    if (field.type === 'yesno') {
        const opts = [
            { v: 'Sí', color: '#10B981' },
            { v: 'No', color: '#EF4444' },
            { v: 'N/A', color: '#9CA3AF' },
        ];
        return (
            <div>
                <label className="text-[10px] uppercase tracking-wider font-bold mb-1.5 block text-gray-500">{field.label}</label>
                <div className="flex gap-2 flex-wrap">
                    {opts.map(({ v, color }) => (
                        <button key={v} type="button" onClick={() => onChange(v)} disabled={disabled}
                            className="px-4 py-2 border text-sm font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                            data-testid={`general-yesno-${field.key}-${v.toLowerCase().replace('/', '')}`}
                            style={{
                                borderColor: value === v ? color : 'var(--border)',
                                background: value === v ? `${color}22` : 'transparent',
                                color: value === v ? color : 'inherit',
                            }}>
                            {v}
                        </button>
                    ))}
                </div>
            </div>
        );
    }
    if (field.type === 'select' && Array.isArray(field.options)) {
        return (
            <div>
                <label className="text-[10px] uppercase tracking-wider font-bold mb-1.5 block text-gray-500">{field.label}</label>
                <select value={value ?? ''} onChange={(e) => onChange(e.target.value)} disabled={disabled}
                    data-testid={`general-select-${field.key}`}
                    className="w-full px-3 py-2 border outline-none text-sm disabled:opacity-50"
                    style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }}>
                    <option value="">Seleccionar...</option>
                    {field.options.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                </select>
            </div>
        );
    }
    return (
        <div>
            <label className="text-[10px] uppercase tracking-wider font-bold mb-1.5 block text-gray-500">
                {field.label}{field.unit ? ` (${field.unit})` : ''}
            </label>
            <input type={field.type === 'number' ? 'number' : 'text'}
                value={value ?? ''} onChange={(e) => onChange(e.target.value)} disabled={disabled}
                data-testid={`general-input-${field.key}`}
                placeholder={field.type === 'number' ? 'Valor numérico...' : 'Escribe aquí...'}
                className="w-full px-3 py-2 border outline-none text-sm disabled:opacity-50"
                style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }} />
        </div>
    );
}

function ChecklistItem({ item, answer, observation, photo, uploadingPhoto, disabled = false, onAnswer, onObs, onPhoto, renderAdminFeedback }) {
    const photoUrl = typeof photo === 'string' ? photo : photo?.url;
    const localPhotoUrl = photo?.localObjectUrl; // Iter 110 · miniatura offline
    // Iter 92 · fix bug crítico de testing_agent: el `onresult` del dictado por
    // voz dispara varios segundos después de `start()`, así que capturar
    // `observation` directo en el closure lo dejaba "congelado" y borraba
    // cualquier texto tecleado mientras grababa. Un ref se lee al MOMENTO en
    // que llega la transcripción, no al momento en que se inició la grabación.
    const obsRef = useRef(observation);
    obsRef.current = observation;
    const opts = [
        { v: 'yes', label: 'Sí', Icon: CheckCircle2, color: '#10B981' },
        { v: 'no', label: 'No', Icon: XCircle, color: '#EF4444' },
        { v: 'na', label: 'N/A', Icon: MinusCircle, color: '#9CA3AF' },
    ];
    const isBlocking = !!item.blocking;
    const blockingFailed = isBlocking && answer === 'no';
    return (
        <div className="border p-4"
            style={{
                borderColor: blockingFailed ? '#DC2626' : (isBlocking ? 'rgba(220,38,38,0.45)' : 'var(--border)'),
                borderWidth: blockingFailed ? 2 : 1,
                background: blockingFailed ? 'rgba(220,38,38,0.04)' : 'transparent',
            }}
            data-testid={`item-${item.id}`}>
            <div className="flex items-start gap-3 mb-3 flex-wrap">
                <span className="text-xs font-mono px-2 py-1 shrink-0" style={{ background: 'rgba(124,107,239,0.15)', color: ACCENT }}>{item.id}</span>
                <p className="flex-1 text-sm min-w-0">{item.question}</p>
                {isBlocking && (
                    <span
                        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-1 shrink-0"
                        style={{ background: 'rgba(220,38,38,0.15)', color: '#DC2626', border: '1px solid rgba(220,38,38,0.45)' }}
                        title="Ítem crítico: un 'no cumple' fuerza REPROBADO en la auditoría, sin importar el score."
                        data-testid={`item-blocking-badge-${item.id}`}
                    >
                        <AlertTriangle className="w-3 h-3" /> Crítico
                    </span>
                )}
                <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 shrink-0" style={{ background: `${WEIGHT_COLOR[item.weight]}22`, color: WEIGHT_COLOR[item.weight] }}>
                    Peso {item.weight} · {WEIGHT_LABEL[item.weight]}
                </span>
            </div>
            <div className="flex flex-wrap gap-2 mb-3">
                {opts.map((o) => (
                    <button key={o.v} type="button" onClick={() => onAnswer(o.v)} disabled={disabled}
                        className="px-4 py-2 border text-xs font-bold inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{
                            borderColor: answer === o.v ? o.color : 'var(--border)',
                            background: answer === o.v ? `${o.color}22` : 'transparent',
                            color: answer === o.v ? o.color : 'inherit',
                        }}
                        data-testid={`answer-${item.id}-${o.v}`}>
                        <o.Icon className="w-3.5 h-3.5" /> {o.label}
                    </button>
                ))}
            </div>
            {blockingFailed && (
                <div className="mb-3 px-3 py-2 text-xs flex items-start gap-2"
                    style={{ background: 'rgba(220,38,38,0.10)', color: '#DC2626', border: '1px solid rgba(220,38,38,0.45)' }}
                    data-testid={`item-blocking-failed-warning-${item.id}`}>
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                    <div>
                        <strong>Esta respuesta forzará el resultado REPROBADO</strong> de la auditoría completa,
                        sin importar el score del resto del checklist. Documenta con foto y observación clara.
                    </div>
                </div>
            )}
            <div className="flex items-center gap-2 mb-2">
                <input type="text" value={observation} onChange={(e) => onObs(e.target.value)} disabled={disabled}
                    placeholder="Observación (opcional)..."
                    className="flex-1 px-3 py-2 border outline-none text-sm disabled:opacity-50"
                    data-testid={`obs-${item.id}`}
                    style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }} />
                <VoiceDictationButton
                    disabled={disabled}
                    testId={`obs-mic-${item.id}`}
                    onResult={(text) => onObs(obsRef.current ? `${obsRef.current} ${text}` : text)}
                />
            </div>
            <div className="flex items-center gap-3">
                <label className={`cursor-pointer inline-flex items-center gap-1.5 px-3 py-1.5 border text-xs font-bold hover:bg-black/5 dark:hover:bg-white/10 ${(uploadingPhoto || disabled) ? 'opacity-60 pointer-events-none' : ''}`}
                    style={{ borderColor: 'var(--border)' }}>
                    {uploadingPhoto ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
                    {uploadingPhoto ? 'Guardando...' : ((photoUrl || localPhotoUrl) ? 'Cambiar foto' : 'Agregar foto')}
                    <input type="file" accept="image/*" capture="environment" className="hidden" disabled={uploadingPhoto || disabled}
                        onChange={onPhoto} data-testid={`photo-${item.id}`} />
                </label>
                {localPhotoUrl ? (
                    <span className="inline-flex items-center gap-2" data-testid={`photo-thumb-local-${item.id}`}>
                        <img src={localPhotoUrl} alt="foto local" className="w-12 h-12 object-cover border" style={{ borderColor: '#B45309' }} />
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded"
                            style={{ background: 'rgba(180,83,9,0.15)', color: '#B45309' }} title="Foto guardada en este dispositivo, pendiente de subir">
                            <HardDriveDownload className="w-3 h-3" /> pendiente
                        </span>
                    </span>
                ) : photoUrl ? (
                    <AuthImage src={photoUrl} className="w-12 h-12 object-cover border" style={{ borderColor: 'var(--border)' }} data-testid={`photo-thumb-${item.id}`} />
                ) : null}
            </div>
            {renderAdminFeedback && renderAdminFeedback(item.id)}
        </div>
    );
}

function AdminFeedbackPanel({ itemId, initial, onCommentPersist, onAiPersist }) {
    const [comment, setComment] = React.useState(initial?.admin_comment || '');
    const [aiOpinion, setAiOpinion] = React.useState(initial?.ai_opinion || '');
    const [aiAt, setAiAt] = React.useState(initial?.ai_opinion_at || null);
    const [saving, setSaving] = React.useState(false);
    const [generating, setGenerating] = React.useState(false);

    React.useEffect(() => {
        setComment(initial?.admin_comment || '');
        setAiOpinion(initial?.ai_opinion || '');
        setAiAt(initial?.ai_opinion_at || null);
    }, [initial?.admin_comment, initial?.ai_opinion, initial?.ai_opinion_at]);

    // Persist admin comment on blur (or on debounce). Only save if changed.
    const persistComment = async () => {
        const trimmed = (comment || '').trim();
        if (trimmed === (initial?.admin_comment || '').trim()) return;
        setSaving(true);
        try {
            const r = await api.patch(`/inspections/${window.__inspectionId}/admin-feedback/${itemId}`, { admin_comment: trimmed });
            onCommentPersist && onCommentPersist(itemId, r.data);
        } catch (e) {
            toast.error(apiErrorMessage(e, 'Error al guardar comentario'));
        } finally { setSaving(false); }
    };

    const generateAi = async () => {
        // Save the current comment first (so AI sees the latest input).
        await persistComment();
        setGenerating(true);
        try {
            const r = await api.post(`/inspections/${window.__inspectionId}/admin-feedback/${itemId}/ai-opinion`);
            setAiOpinion(r.data.ai_opinion || '');
            setAiAt(r.data.ai_opinion_at || null);
            onAiPersist && onAiPersist(itemId, r.data);
            toast.success('Sugerencia de IA generada.');
        } catch (e) {
            toast.error(apiErrorMessage(e, 'No se pudo generar la opinión de IA'));
        } finally { setGenerating(false); }
    };

    return (
        <div
            className="mt-4 p-3 border-l-2 space-y-2"
            style={{ borderColor: ACCENT, background: 'rgba(124,107,239,0.04)' }}
            data-testid={`admin-feedback-${itemId}`}
        >
            <div className="text-[10px] uppercase tracking-wider font-bold" style={{ color: ACCENT }}>
                Retroalimentación del Administrador
            </div>
            <textarea
                rows={2}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                onBlur={persistComment}
                placeholder="Escribe tu opinión sobre esta observación..."
                data-testid={`admin-feedback-comment-${itemId}`}
                className="w-full px-3 py-2 border outline-none text-sm resize-none"
                style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }}
            />
            <div className="flex items-center gap-2 flex-wrap">
                <button
                    type="button"
                    onClick={generateAi}
                    disabled={generating}
                    data-testid={`admin-feedback-ai-btn-${itemId}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
                    style={{ background: ACCENT }}
                >
                    {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                    {aiOpinion ? 'Regenerar opinión de IA' : 'Generar opinión de IA'}
                </button>
                {saving && (
                    <span className="text-[11px] text-gray-400 inline-flex items-center gap-1">
                        <Loader2 className="w-3 h-3 animate-spin" /> Guardando…
                    </span>
                )}
            </div>
            {aiOpinion && (
                <div
                    className="p-3 border text-sm relative"
                    style={{ borderColor: ACCENT, background: 'linear-gradient(135deg, rgba(124,107,239,0.10), rgba(124,107,239,0.02))' }}
                    data-testid={`admin-feedback-ai-opinion-${itemId}`}
                >
                    <div className="flex items-center gap-1.5 mb-1">
                        <Sparkles className="w-3.5 h-3.5" style={{ color: ACCENT }} />
                        <span className="text-[10px] uppercase tracking-wider font-bold" style={{ color: ACCENT }}>
                            Sugerencia de IA
                        </span>
                        {aiAt && <span className="text-[10px] text-gray-400 ml-auto">{formatSince(aiAt)}</span>}
                    </div>
                    <p className="whitespace-pre-wrap leading-relaxed">{aiOpinion}</p>
                </div>
            )}
        </div>
    );
}
